import { type SugarProviderPhoto } from '@app/types'
import { SimpleGrid, Stack, Text } from '@mantine/core'
import { type FC, type RefObject } from 'react'

import { AddPhotoTile } from './AddPhotoTile'
import { PhotoTile } from './PhotoTile'
import { type StagedPhoto } from './stagedReducer'
import { StagedPhotoTile } from './StagedPhotoTile'

export interface PhotoGalleryProps {
  addDisabled?: boolean
  addOpenRef?: RefObject<() => void | undefined>
  atCap: boolean
  cap: number
  isBusy?: boolean
  onAdd: (file: File) => void
  onRemoveServer: (photo: SugarProviderPhoto) => void
  onRemoveStaged: (id: string) => void
  onReplaceServer: (photo: SugarProviderPhoto) => void
  onRetryStaged?: (id: string) => void
  photos: SugarProviderPhoto[]
  staged: StagedPhoto[]
}

export const PhotoGallery: FC<PhotoGalleryProps> = ({
  addDisabled = false,
  addOpenRef,
  atCap,
  cap,
  isBusy = false,
  onAdd,
  onRemoveServer,
  onRemoveStaged,
  onReplaceServer,
  onRetryStaged,
  photos,
  staged
}) => {
  const isEmpty = photos.length === 0 && staged.length === 0

  if (isEmpty) {
    return (
      <Stack align='center' spacing='md' py='xl'>
        <Text size='sm' c='dimmed' ta='center'>
          Add photos that represent your program.
        </Text>
        <div style={{ width: 200 }}>
          <AddPhotoTile disabled={addDisabled} onAdd={onAdd} openRef={addOpenRef} />
        </div>
      </Stack>
    )
  }

  return (
    <Stack spacing='md'>
      <SimpleGrid breakpoints={[
        { cols: 1, maxWidth: 'xs' },
        { cols: 2, minWidth: 'xs' },
        { cols: 3, minWidth: 'sm' }
      ]}>
        {photos.map((photo) => (
          <PhotoTile
            disabled={isBusy}
            key={photo.id}
            onRemove={() => onRemoveServer(photo)}
            onReplace={() => onReplaceServer(photo)}
            photo={photo}
          />
        ))}
        {staged.map((s) => (
          <StagedPhotoTile
            disabled={isBusy && s.status !== 'error'}
            key={s.id}
            onRemove={() => onRemoveStaged(s.id)}
            onRetry={onRetryStaged ? () => onRetryStaged(s.id) : undefined}
            staged={s}
          />
        ))}
        <div style={{ display: atCap ? 'none' : undefined }}>
          <AddPhotoTile disabled={addDisabled} onAdd={onAdd} openRef={addOpenRef} />
        </div>
      </SimpleGrid>
      {atCap && (
        <Text c='dimmed' size='sm'>You have reached the maximum of {cap} photos.</Text>
      )}
    </Stack>
  )
}
